import { getLicensingViewOrManagePermissions } from '@experiences/interfaces';
import Box from '@mui/material/Box';
import { PortalErrorPage } from '@uipath/portal-shell-react';
import type { PropsWithChildren } from 'react';
import React, {
    useContext,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useCanViewTenantLicensing } from '../../component/licensing/useCanManageLicensingData';
import { isOrgAdminSelector } from '../../store/selectors';
import {
    useHasAnyPermission,
    UserAuthPDPModelContext,
} from '../UserAuthPDPModelProvider/UserAuthPDPModelProvider';

/**
 * Wraps licensing routes. Shows shell 403 when the user holds none of:
 *  - org admin (short-circuit via {@link useHasAnyPermission}),
 *  - Licensing **View** or **Manage** PDP actions (org-scoped),
 *  - `Licensing.TenantLicensingData.View` for the tenant in the route (when on a tenant route).
 *
 * On a tenant-scoped URL (`/admin/tenant/:tenantId/...`), the tenant viewer check is restricted to that
 * tenant's scope — preventing a user with the action on tenant A from navigating to tenant B's licensing
 * page. On org-scoped URLs (no `:tenantId` param), the tenant viewer check is omitted entirely; only org
 * admin or org Licensing View/Manage holders pass.
 */
export const LicensesRouteAccessGuard: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();
    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const pdpModel = useContext(UserAuthPDPModelContext);
    const { tenantId } = useParams<{ tenantId?: string }>();
    const licensingViewOrManagePermissions = useMemo(() => getLicensingViewOrManagePermissions(), []);
    const hasLicensingViewOrManage = useHasAnyPermission(licensingViewOrManagePermissions);
    const canViewTenantLicensing = useCanViewTenantLicensing(tenantId);

    if (isOrgAdmin) {
        return <>
            {children}
        </>;
    }

    if (pdpModel === undefined) {
        return null;
    }

    const tenantViewerAllowed = !!tenantId && canViewTenantLicensing;

    if (hasLicensingViewOrManage || tenantViewerAllowed) {
        return <>
            {children}
        </>;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100%',
                width: '100%',
            }}
        >
            <PortalErrorPage
                type="403"
                buttonText={translate({ id: 'CLIENT_REFRESH_PAGE' })}
                buttonHref={globalThis.location.href}
            />
        </Box>
    );
};
